import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { queryKeysInvoiceReprocessing } from '../queryKeys';

import {
  type UpdateStopperRuleMutationArguments,
  type UpdateStopperRuleResponse,
} from './types';
import { updateInvoiceProcessStopperRule } from './updateInvoiceProcessStopperRule';

/**
 * A custom hook to update a stopper rule for an invoice process.
 * Uses the `useMutation` hook from `@tanstack/react-query` internally.
 * @returns A promise that resolves when the stopper rule is updated.
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Invoice/operation/update_invoice_process_stopper_rule_vadmin_invoice_stopper_rule__stopper_rule_id__put
 */
export const useMutationUpdateInvoiceProcessStopperRule = (
  options?: UseMutationOptions<
    UpdateStopperRuleResponse,
    Error,
    UpdateStopperRuleMutationArguments
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      stopperRuleId,
      updateStopperRulePayload,
    }: UpdateStopperRuleMutationArguments) =>
      updateInvoiceProcessStopperRule({
        stopperRuleId,
        updateStopperRulePayload,
      }),
    onSettled(data, error, { stopperRuleId }) {
      queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceReprocessing.getStopperRule(stopperRuleId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceReprocessing.getStopperRules({}),
      });
    },
    ...options,
  });
};
